import React, { Dispatch, Fragment, SetStateAction, useRef, useState } from 'react';
import Drawer from '../../../common/components/Drawer/Drawer';
import { calculateMovesPercentage, getCarrierById } from '../../../common/helpers/carriers';
import { modifyDate } from '../../../common/helpers/modify-date';
import { formattedNumberToInt } from '../../../common/helpers/utils';
import { AdjustableComponent, useClasses } from '../../../common/hooks/useClasses';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import FormikInputField from '../../../common/components/FormikInputField/FormikInputField';
import { McButton, McNotification, McTab, McTabBar, McTable } from '@maersk-global/mds-react-wrapper';
import z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import FormattedMessage from '../../../common/FormattedMessage';
import {
  editCarriersMapper,
  recalculateEntries,
  recalculateEntriesForYard,
  updateEntries,
  validateEntries,
  validateEntriesForYard,
} from '../../config/mappers';
import useCapacityData from '../../hooks/useCapacityData';
import useCarrierColumnsHeader from '../../hooks/useCarrierColumnsHeader';
import ChangelogDrawer from '../changelog/changelog-drawer/ChangelogDrawer';
import { createWeeklyValueDto } from '../dto/WeeklyValueDto';
import IMOInputField from '../IMODescription/IMOInputField';
import { editCarriersWeeklyTableColumns, editCarriersYardTableColumns } from '../soc/const';

export type EditWeeklyValuesDrawerStyles = {
  title: string;
  field: string;
  buttons: string;
  form: string;
};

export type EditWeeklyValuesDrawerProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  selectedTerminal: string;
  defaultValues: Map<string, number>;
  selectedWeeks: string[];
  type: 'soc' | 'yard';
  onSuccess?: Function;
};

const FormValidationSchemaSOC = z
  .object({
    terminalCode: z.string(),
    moves: z.number({ message: 'Value should be a number' }).positive(),
    moveCountStretchPercentage: z.number({ message: 'Value should be a number (0 - 100)' }).min(0).max(100),
    moveCountCriticalPercentage: z.number({ message: 'Value should be a number (0 - 100)' }).min(0).max(100),
    entries: z.array(
      z.object({
        moves: z.number().gte(0, 'Movecount must be greater than or equal 0'),
        capacityDataType: z.string(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    // @ts-ignore
    if (!validateEntries(data.entries, data.moves)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Residual value can not be less than 0',
        path: ['entries'],
      });
    }
  });

const FormValidationSchemaYard = z
  .object({
    terminalCode: z.string(),
    oogUnits: z.number({ message: 'Value should be a number' }).positive(),
    imoUnits: z.number({ message: 'Value should be a number' }).positive(),
    yardCapacity: z.number({ message: 'Value should be a number' }).positive(),
    reeferPlugs: z.number({ message: 'Value should be a number' }).positive(),
    entries: z.array(
      z.object({
        yardCapacity: z.number().gte(0, 'Yard Capacity must be greater than or equal 0'),
        reeferPlugs: z.number().gte(0, 'Reefer Plugs must be greater than or equal 0'),
        oogUnits: z.number().gte(0, 'OOG Units must be greater than or equal 0'),
        imoUnits: z.number().gte(0, 'IMO Units must be greater than or equal 0'),
        capacityDataType: z.string(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    if (
      // @ts-ignore
      !validateEntriesForYard(data.entries, {
        yardCapacity: data.yardCapacity,
        reeferPlugs: data.reeferPlugs,
        imoUnits: data.imoUnits,
        oogUnits: data.oogUnits,
      })
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Residual value can not be less than 0',
        path: ['entries'],
      });
    }
  });

const EditWeeklyValuesDrawer: AdjustableComponent<EditWeeklyValuesDrawerProps, EditWeeklyValuesDrawerStyles> = ({
  classes,
  open,
  setOpen,
  selectedTerminal,
  selectedWeeks,
  defaultValues,
  type,
  onSuccess,
}) => {
  const styles = useClasses(
    {
      title: 'EditWeeklyValuesDrawer__title',
      field: 'EditWeeklyValuesDrawer__field',
      buttons: 'EditWeeklyValuesDrawer__buttons',
      form: 'EditWeeklyValuesDrawer__form-wrapper',
    },
    classes,
  );
  const { updateCapacityWithCarriers } = useCapacityData();
  const formikRef = useRef(null);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [alertShown, setAlertShown] = useState(false);
  const { getCustomHeaders } = useCarrierColumnsHeader();

  const onClose = () => {
    setOpen(false);
    setAlertShown(false);
    setActiveTab(0);
  };

  const getFormInitialValues = () => {
    const baseValues = {
      terminalCode: selectedTerminal,
      selectedWeeks: selectedWeeks,
      capacityDataType: defaultValues['capacityDataType'],
      entries: defaultValues['entries'],
    };
    const socValues = {
      moves: formattedNumberToInt(defaultValues['moves']),
      moveCountStretchPercentage: defaultValues['moveCountStretchPercentage'],
      moveCountCriticalPercentage: defaultValues['moveCountCriticalPercentage'],
    };
    const yardValues = {
      yardCapacity: formattedNumberToInt(defaultValues['yardCapacity']),
      reeferPlugs: formattedNumberToInt(defaultValues['reeferPlugs']),
      imoUnits: formattedNumberToInt(defaultValues['imoUnits']),
      imoUnitsDescription: defaultValues['imoUnitsDescription'],
      oogUnits: formattedNumberToInt(defaultValues['oogUnits']),
      ladenCapacity: formattedNumberToInt(defaultValues['ladenCapacity']) || '',
      emptyCapacity: formattedNumberToInt(defaultValues['emptyCapacity']) || '',
      maxCapacity: formattedNumberToInt(defaultValues['maxCapacity']) || '',
    };
    return { ...baseValues, ...(type === 'soc' ? socValues : yardValues) };
  };

  const onSubmit = async (values, { setSubmitting }) => {
    if (values.entries[0].overwrite && activeTab === 0) {
      setAlertShown(true);
      setActiveTab(1);
      return;
    }
    setSubmitting(true);
    const dto = createWeeklyValueDto(values, type);
    try {
      // @ts-ignore
      await updateCapacityWithCarriers(dto);
      onSuccess?.();
      onClose();
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={'Edit weekly values'}
      open={open}
      setOpen={onClose}
      onRequestClose={onClose}
      noFooter
      wider
      titleAddon={
        selectedWeeks.length === 1 ? (
          <div className="EditWeeklyValuesDrawer__changelog">
            <McButton
              type="button"
              appearance="secondary"
              variant="outlined"
              click={() => setChangelogOpen(true)}
              icon="arrow-anti-clockwise-clock"
            >
              <FormattedMessage id="changelog" />
            </McButton>
          </div>
        ) : null
      }
    >
      <Formik
        initialValues={getFormInitialValues()}
        enableReinitialize
        //@ts-ignore
        validationSchema={toFormikValidationSchema(type === 'soc' ? FormValidationSchemaSOC : FormValidationSchemaYard)}
        onSubmit={onSubmit}
        innerRef={formikRef}
      >
        {({ isSubmitting, values, setFieldValue }) => {
          const carrierData = editCarriersMapper(
            type,
            // @ts-ignore
            values.entries,
            type === 'soc'
              ? {
                  // @ts-ignore
                  totalMoveCount: values.moves,
                  // @ts-ignore
                  stretch: values.moveCountStretchPercentage,
                  // @ts-ignore
                  critical: values.moveCountCriticalPercentage,
                }
              : {
                  // @ts-ignore
                  yardCapacity: values.yardCapacity,
                  // @ts-ignore
                  reeferPlugs: values.reeferPlugs,
                  // @ts-ignore
                  imoUnits: values.imoUnits,
                  // @ts-ignore
                  oogUnits: values.oogUnits,
                },
          );
          return (
            <Form className="EditDefaultValuesDrawer__form">
              <div className="EditDefaultValuesDrawer__form__wrapper">
                <McTabBar tabchange={(e) => setActiveTab(e.detail)} currentindex={activeTab}>
                  <McTab slot="tab" label="Weekly values" />
                  <div slot="panel">
                    <div>
                      <h3 className={styles.title}>{selectedWeeks.join(', ')}</h3>
                      <hr />
                      <br />

                      {type === 'soc' ? (
                        <div style={{ backgroundColor: '#f0f0f0', padding: '0.5rem', borderRadius: '0.25rem' }}>
                          <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
                            {/* Move Count */}
                            <div style={{ flexBasis: '30%', textAlign: 'left' }}>
                              <label>Move count</label>
                              <Field
                                label={''}
                                type="number"
                                name="moves"
                                component={FormikInputField}
                                input={(e) => {
                                  setFieldValue('moves', parseInt(e.target.value));
                                  setFieldValue(
                                    'entries',
                                    recalculateEntries(
                                      // @ts-ignore
                                      values.entries,
                                      // @ts-ignore
                                      getFormInitialValues().entries,
                                      // @ts-ignore
                                      getFormInitialValues().moves,
                                      e.target.value,
                                    ),
                                  );
                                }}
                              />
                              <ErrorMessage name="moves" component="div" />
                            </div>

                            {/* Threshold #1 with Icon and Total */}
                            <div style={{ flexBasis: '30%', textAlign: 'left' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ backgroundColor: '#FFD029', width: '1rem', height: '1rem' }}></span>
                                <label>Threshold L2</label>
                              </div>
                              <Field
                                label={''}
                                type="number"
                                name="moveCountStretchPercentage"
                                suffix="%"
                                component={FormikInputField}
                              />
                              <ErrorMessage name="moveCountStretchPercentage" component="div" />
                              <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#8A8A90' }}>
                                {/*// @ts-ignore*/}
                                Total: {printTotal(values.moves, values.moveCountStretchPercentage)}
                              </div>
                            </div>

                            {/* Threshold #2 with Icon and Total */}
                            <div style={{ flexBasis: '30%', textAlign: 'left' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ backgroundColor: '#FF6000', width: '1rem', height: '1rem' }}></span>
                                <label>Threshold L3</label>
                              </div>
                              <Field
                                label={''}
                                type="number"
                                name="moveCountCriticalPercentage"
                                suffix="%"
                                component={FormikInputField}
                              />
                              <ErrorMessage name="moveCountCriticalPercentage" component="div" />
                              <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#8A8A90' }}>
                                {/*// @ts-ignore*/}
                                Total: {printTotal(values.moves, values.moveCountCriticalPercentage)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <Field
                              label={'Yard TEU capacity'}
                              type="number"
                              name="yardCapacity"
                              component={FormikInputField}
                              input={(e) => {
                                setFieldValue('yardCapacity', parseInt(e.target.value));
                                setFieldValue(
                                  'entries',
                                  recalculateEntriesForYard(
                                    // @ts-ignore
                                    values.entries,
                                    // @ts-ignore
                                    getFormInitialValues().entries,
                                    'yardCapacity',
                                    // @ts-ignore
                                    getFormInitialValues().yardCapacity,
                                    parseInt(e.target.value),
                                  ),
                                );
                              }}
                            />
                            <ErrorMessage name="yardCapacity" component="div" />
                            <Field
                              label={'Reefer plugs'}
                              type="number"
                              name="reeferPlugs"
                              component={FormikInputField}
                              input={(e) => {
                                setFieldValue('reeferPlugs', parseInt(e.target.value));
                                setFieldValue(
                                  'entries',
                                  recalculateEntriesForYard(
                                    // @ts-ignore
                                    values.entries,
                                    // @ts-ignore
                                    getFormInitialValues().entries,
                                    'reeferPlugs',
                                    // @ts-ignore
                                    getFormInitialValues().reeferPlugs,
                                    parseInt(e.target.value),
                                  ),
                                );
                              }}
                            />
                            <ErrorMessage name="reeferPlugs" component="div" />
                          </div>

                          <br />
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <IMOInputField
                              isDrawerOpen={open}
                              setFieldValue={setFieldValue}
                              // @ts-ignore
                              entries={values.entries}
                              // @ts-ignore
                              defaultEntries={getFormInitialValues().entries}
                              // @ts-ignore
                              defaultImoUnits={getFormInitialValues().imoUnits}
                            />
                            <div>
                              <Field
                                label={'OOG units'}
                                type="number"
                                name="oogUnits"
                                component={FormikInputField}
                                input={(e) => {
                                  setFieldValue('oogUnits', parseInt(e.target.value));
                                  setFieldValue(
                                    'entries',
                                    recalculateEntriesForYard(
                                      // @ts-ignore
                                      values.entries,
                                      // @ts-ignore
                                      getFormInitialValues().entries,
                                      'oogUnits',
                                      // @ts-ignore
                                      getFormInitialValues().oogUnits,
                                      parseInt(e.target.value),
                                    ),
                                  );
                                }}
                              />
                              <ErrorMessage className="EventsDrawer__error" name="oogUnits" component="div" />
                            </div>
                          </div>

                          <br />
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <Field label="Laden TEUs" type="number" name="ladenCapacity" component={FormikInputField} />
                            <ErrorMessage name="ladenCapacity" component="div" />
                            <Field
                              label={'Empty TEUs'}
                              type="number"
                              name="emptyCapacity"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="emptyCapacity" component="div" />
                            <Field
                              label={'Max TEU Capacity'}
                              type="number"
                              name="maxCapacity"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="maxCapacity" component="div" />
                          </div>
                        </>
                      )}

                      <Field type="hidden" name="hubId" />
                    </div>
                  </div>
                  <McTab slot="tab" label="Carriers" />
                  <div slot="panel">
                    {alertShown && (
                      <div className="EditDefaultValuesDrawer__carrier-warning">
                        <McNotification
                          appearance="warning"
                          body="Please confirm carriers' move count before submitting"
                          icon="exclamation-triangle"
                          verticalalign="middle"
                          actionsposition="right"
                          fit="small"
                        />
                      </div>
                    )}
                    <McTable
                      columns={type === 'soc' ? editCarriersWeeklyTableColumns : editCarriersYardTableColumns}
                      data={carrierData}
                    >
                      {getCustomHeaders().map((header) => (
                        <div key={header.id} slot={`${header.id}_header`}>
                          {header.render()}
                        </div>
                      ))}
                      {carrierData.map((row, index) => {
                        const carrier = getCarrierById(row.carrier);
                        return (
                          <Fragment key={`${row.id}_actions`}>
                            <div slot={`${row.id}_carrier`}>
                              <div className="TerminalCarriersRenderer__logo">
                                {carrier.image && <carrier.image />}
                                <span className="TerminalCarriersRenderer__logo__name">{carrier.name}</span>
                              </div>
                            </div>
                            <div slot={`${row.id}_moveCount`}>
                              {carrier.order > -1 ? (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <Field
                                    hiddenlabel
                                    name="moveCount"
                                    type="number"
                                    fit="small"
                                    component={FormikInputField}
                                    value={row.moveCount}
                                    input={(e: InputEvent) =>
                                      setFieldValue(
                                        'entries',
                                        // @ts-ignore
                                        updateEntries(values.entries, carrier.id, 'moves', e.target.value),
                                      )
                                    }
                                    min={0}
                                    width={100}
                                  />
                                  <Field
                                    hiddenlabel
                                    type="number"
                                    name="moveCountPercentage"
                                    suffix="%"
                                    component={FormikInputField}
                                    min={0}
                                    fit="small"
                                    width={80}
                                    value={calculateMovesPercentage(
                                      formattedNumberToInt(`${carrierData[0].moveCount}`),
                                      row.moveCount,
                                    )}
                                    input={(e: InputEvent) =>
                                      setFieldValue(
                                        'entries',
                                        updateEntries(
                                          // @ts-ignore
                                          values.entries,
                                          carrier.id,
                                          'percentage',
                                          // @ts-ignore
                                          e.target.value,
                                          formattedNumberToInt(`${carrierData[0].moveCount}`),
                                        ),
                                      )
                                    }
                                  />
                                </div>
                              ) : (
                                row.moveCount
                              )}
                            </div>
                            <div slot={`${row.id}_yardCapacity`}>
                              {carrier.order > -1 ? (
                                <div>
                                  <Field
                                    hiddenlabel
                                    name="yardCapacity"
                                    type="number"
                                    fit="small"
                                    component={FormikInputField}
                                    value={row.yardCapacity}
                                    input={(e: InputEvent) =>
                                      setFieldValue(
                                        'entries',
                                        // @ts-ignore
                                        updateEntries(values.entries, carrier.id, 'yardCapacity', e.target.value),
                                      )
                                    }
                                    min={0}
                                    width={100}
                                  />
                                </div>
                              ) : (
                                row.yardCapacity
                              )}
                            </div>
                            <div slot={`${row.id}_reeferPlugs`}>
                              {carrier.order > -1 ? (
                                <div>
                                  <Field
                                    hiddenlabel
                                    name="reeferPlugs"
                                    type="number"
                                    fit="small"
                                    component={FormikInputField}
                                    value={row.reeferPlugs}
                                    input={(e: InputEvent) =>
                                      setFieldValue(
                                        'entries',
                                        // @ts-ignore
                                        updateEntries(values.entries, carrier.id, 'reeferPlugs', e.target.value),
                                      )
                                    }
                                    min={0}
                                    width={100}
                                  />
                                </div>
                              ) : (
                                row.reeferPlugs
                              )}
                            </div>
                            <div slot={`${row.id}_imoUnits`}>
                              {carrier.order > -1 ? (
                                <div>
                                  <Field
                                    hiddenlabel
                                    name="imoUnits"
                                    type="number"
                                    fit="small"
                                    component={FormikInputField}
                                    value={row.imoUnits}
                                    input={(e: InputEvent) =>
                                      setFieldValue(
                                        'entries',
                                        // @ts-ignore
                                        updateEntries(values.entries, carrier.id, 'imoUnits', e.target.value),
                                      )
                                    }
                                    min={0}
                                    width={100}
                                  />
                                </div>
                              ) : (
                                row.imoUnits
                              )}
                            </div>
                            <div slot={`${row.id}_oogUnits`}>
                              {carrier.order > -1 ? (
                                <div>
                                  <Field
                                    hiddenlabel
                                    name="oogUnits"
                                    type="number"
                                    fit="small"
                                    component={FormikInputField}
                                    value={row.oogUnits}
                                    input={(e: InputEvent) =>
                                      setFieldValue(
                                        'entries',
                                        // @ts-ignore
                                        updateEntries(values.entries, carrier.id, 'oogUnits', e.target.value),
                                      )
                                    }
                                    min={0}
                                    width={100}
                                  />
                                </div>
                              ) : (
                                row.oogUnits
                              )}
                            </div>
                          </Fragment>
                        );
                      })}
                    </McTable>
                    <ErrorMessage className="EventsDrawer__error" name="entries" component="div" />
                  </div>
                </McTabBar>
              </div>
              <div className={styles.buttons}>
                <McButton type="button" appearance="neutral" click={() => onClose()}>
                  <FormattedMessage id="cancel" />
                </McButton>
                <McButton disabled={isSubmitting} type="submit" appearance="primary">
                  <FormattedMessage
                    id={isSubmitting ? 'updateRuleDrawerFormSubmitting' : 'updateRuleDrawerFormSubmit'}
                  />
                </McButton>
              </div>
            </Form>
          );
        }}
      </Formik>

      <ChangelogDrawer
        open={changelogOpen}
        setOpen={setChangelogOpen}
        id={defaultValues['id']}
        service="capacity"
        wider
      />
    </Drawer>
  );
};

const printTotal = (moves: number, percentage: number) => {
  return moves && percentage ? moves + Math.ceil((moves * percentage) / 100) : '-';
};

export default EditWeeklyValuesDrawer;
